/**
 * @file themeColors.ts
 * @description Design-system-token-derived color palette for the "Ask Gemini"
 *   chat surfaces.
 * @module knowledge-graph/constants
 * @remarks
 * SE-5358 (Strategy C) re-skinned the chat off its bespoke iMessage hex palette
 * onto the closed semantic design-system tier. Every value below is the resolved
 * hex of a NAMED semantic token (docs/design-system/tokens.md), light/dark.
 *
 * Why a resolved-hex object instead of NativeWind `className` tokens:
 * the chat's remaining color consumers feed **third-party libraries** that
 * require literal color strings and cannot accept Tailwind classes —
 * `react-native-gifted-chat`'s `Bubble`/`textInputProps` style props and
 * `@ronradtke/react-native-markdown-display`'s `style` object — plus the
 * `react-native` `TextInput` send-button color. Atom-rendered surfaces
 * (ProgressMessageBubble, QueryProgressBar) were migrated to `className`
 * tokens + `dark:` variants directly and no longer read this object.
 *
 * The hexes are kept in lockstep with the semantic tier defined in
 * `src/components/ui/gluestack-ui-provider/config.ts` /
 * `tailwind.config.js`. Each property names the token it mirrors so the two
 * stay reconcilable; changing a token there means updating the matching hex
 * here. This is the single auditable chat→token mapping (replacing the old
 * bespoke palette).
 */

/**
 * Resolved hex values for the semantic design-system tokens the chat uses.
 * Keyed by token name; the chat palette below is composed entirely from these.
 * @remarks Mirrors the semantic tier in gluestack config / tailwind.config.js;
 *   the `// token` / RGB annotations make each value reconcilable to its source.
 */
const TOKENS = {
  light: {
    accentPrimary: "#007AFF", // accent-primary — rgb(0 122 255), iOS system blue
    contentOnAccent: "#FFFFFF", // content-on-accent
    contentPrimary: "#262627", // content-primary — typography-900 rgb(38 38 39)
    contentSecondary: "#525252", // content-secondary — typography-700 rgb(82 82 82)
    contentMuted: "#737373", // content-muted — --color-content-muted rgb(115 115 115) (SE-8550)
    contentInverse: "#FEFEFF", // content-inverse — typography-0 rgb(254 254 255)
    surfaceBase: "#FFFFFF", // surface-base — background-0 rgb(255 255 255)
    surfaceSubtle: "#F2F1F1", // surface-subtle — background-100 rgb(242 241 241)
    surfaceMuted: "#DCDBDB", // surface-muted — background-200 rgb(220 219 219)
    surfaceAction: "#2C2C2E", // surface-action — --color-surface-action rgb(44 44 46) (SE-8551)
    surfaceInverse: "#181718", // surface-inverse — background-950 rgb(24 23 24)
    outlineDefault: "#DDDCDB", // outline-default — outline-200 rgb(221 220 219)
    outlineSubtle: "#E6E6E6", // outline-subtle — outline-100 rgb(230 230 230)
    statusSuccess: "#348352", // status-success — success-500 rgb(52 131 82)
    statusError: "#E63535", // status-error — error-500 rgb(230 53 53)
  },
  dark: {
    accentPrimary: "#0A84FF", // accent-primary — rgb(10 132 255), iOS dark blue
    contentOnAccent: "#FFFFFF", // content-on-accent
    contentPrimary: "#F5F5F5", // content-primary — typography-900 rgb(245 245 245)
    contentSecondary: "#DBDBDC", // content-secondary — typography-700 rgb(219 219 220)
    contentMuted: "#A3A3A3", // content-muted — typography-500 rgb(163 163 163)
    contentInverse: "#171717", // content-inverse — typography-0 rgb(23 23 23)
    surfaceBase: "#121212", // surface-base — background-0 rgb(18 18 18)
    surfaceSubtle: "#414040", // surface-subtle — background-100 rgb(65 64 64)
    surfaceMuted: "#535252", // surface-muted — background-200 rgb(83 82 82)
    surfaceAction: "#2C2C2E", // surface-action — --color-surface-action rgb(44 44 46) (SE-8551)
    surfaceInverse: "#FEFEFE", // surface-inverse — background-950 rgb(254 254 254)
    outlineDefault: "#535252", // outline-default — outline-200 rgb(83 82 82)
    outlineSubtle: "#414141", // outline-subtle — outline-100 rgb(65 65 65)
    statusSuccess: "#489766", // status-success — success-500 rgb(72 151 102)
    statusError: "#EF4444", // status-error — error-500 rgb(239 68 68)
  },
} as const;

/**
 * The set of resolved semantic-token hex values for a single theme.
 * @remarks Keys mirror the named tokens consumed by the chat palette; widened
 *   to `string` (vs the literal `as const` types) so both light and dark token
 *   sets satisfy `buildChatColors`.
 */
type ThemeTokenSet = Record<keyof (typeof TOKENS)["light"], string>;

/**
 * Builds the chat palette for one theme entirely from semantic tokens.
 * @param t - The resolved token set for the active theme (light or dark)
 * @returns The chat color palette (progressBar / markdown / messageBubble)
 *   composed from semantic design-system tokens
 * @remarks Property comments map each chat role to the SE-5358 token decision.
 */
const buildChatColors = (t: ThemeTokenSet) => ({
  // Progress Bar — token-mapped (SE-5358)
  progressBar: {
    background: t.surfaceBase, // surface-base
    border: t.outlineDefault, // outline-default
    trackBackground: t.surfaceSubtle, // surface-subtle
    fill: t.accentPrimary, // accent-primary
    complete: t.statusSuccess, // status-success
    error: t.statusError, // status-error
    text: t.contentPrimary, // content-primary
    counter: t.contentMuted, // content-muted
  },
  // Markdown — token-mapped (SE-5358)
  markdown: {
    background: "transparent",
    text: t.contentSecondary, // content-secondary
    heading: t.contentPrimary, // content-primary
    emphasis: t.contentPrimary, // content-primary
    link: t.accentPrimary, // accent-primary (links recolor to iOS blue — approved)
    codespan: {
      background: t.surfaceSubtle, // surface-subtle
      text: t.contentSecondary, // content-secondary
    },
    codeblock: {
      // SE-5358 follow-up: code blocks must read as dark slate in BOTH themes.
      // surface-inverse / content-inverse FLIP per theme (so dark mode rendered
      // a near-white block). surface-action (#2C2C2E) and content-on-accent
      // (#FFFFFF) are fixed across light + dark, keeping code blocks dark.
      background: t.surfaceAction, // surface-action (theme-stable #2C2C2E)
      text: t.contentOnAccent, // content-on-accent (theme-stable #FFFFFF)
    },
    blockquote: {
      background: t.surfaceBase, // surface-base
      border: t.outlineSubtle, // outline-subtle
    },
    table: {
      background: t.surfaceBase, // surface-base
      // SE-5558: header row needs its own surface so `th` reads as a header
      // instead of inheriting the library's `#000000`-bordered default.
      headerBackground: t.surfaceSubtle, // surface-subtle
      border: t.outlineSubtle, // outline-subtle
    },
    divider: t.outlineSubtle, // outline-subtle
  },
  // Message Bubble — token-mapped (SE-5358)
  messageBubble: {
    background: t.surfaceMuted, // surface-muted (received bubble)
    userBackground: t.accentPrimary, // accent-primary (sent bubble)
    activeBackground: t.surfaceBase, // surface-base
    text: t.contentPrimary, // content-primary
    userText: t.contentOnAccent, // content-on-accent
    subtext: t.contentMuted, // content-muted
    border: {
      // SE-8272: the AI answer's wrapper carried only a background fill, so
      // it read as an unstyled flat rectangle with nothing distinguishing it
      // from the page. `outline-default` is the same token the progress bar
      // card already borders itself with (see progressBar.border above).
      default: t.outlineDefault, // outline-default
      active: t.accentPrimary, // accent-primary
      complete: t.statusSuccess, // status-success
    },
  },
});

/**
 * Theme-aware color constants for Ask Gemini chat components.
 * Composed entirely from the semantic design-system tier (SE-5358 Strategy C).
 */
export const chatThemeColors = {
  light: buildChatColors(TOKENS.light),
  dark: buildChatColors(TOKENS.dark),
} as const;

/**
 * Discriminator for the available chat theme keys (light | dark).
 */
export type ChatTheme = keyof typeof chatThemeColors;
/**
 * Resolved chat color palette for a single theme.
 */
export type ChatThemeColors = (typeof chatThemeColors)[ChatTheme];
